// API Client for STOVE Online Ordering
// In production: nginx proxies /sys/ to external.stovepos.com
// For direct API access, set VITE_API_BASE=https://external.stovepos.com

const API_BASE = import.meta.env.VITE_API_BASE || '';

// Helper for API calls
async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API Error:', endpoint, error);
    throw error;
  }
}

export const api = {
  // Get venue data with online settings, order types, tip suggestions
  async getOnlineData(venueid) {
    return request(`/sys/v4/venue/getOnlineData?venueid=${venueid}`);
  },

  // Get venue info (legacy endpoint, still works)
  async getOnlineVenueInfo(venueid) {
    return request(`/sys/v4/venue/getOnlineVenueInfo?venueid=${venueid}`);
  },

  // Get menu categories, items, modifiers
  async getMenu(venueid) {
    return request(`/sys/v4/menu/getMenu?venueid=${venueid}`);
  },

  // ---- Loyalty (online ordering) ----
  // Program + enrollment + points + tier + subscription + rewards + surprises.
  async getLoyaltyStatus(venueid, customerID) {
    const q = customerID
      ? `?venueid=${venueid}&customerID=${customerID}`
      : `?venueid=${venueid}`;
    return request(`/sys/v4/loyalty/online/status${q}`);
  },
  // Enroll a returning customer (awards signup bonus).
  async joinLoyalty(venueid, customerID) {
    return request(`/sys/v4/loyalty/online/join`, {
      method: 'POST', body: JSON.stringify({ venueid, customerID })
    });
  },
  // Preview a redemption discount (no writes). payload: { venueid, customerID, subtotal, points?|reward_id?|surprise_id? }
  async loyaltyRedeemPreview(payload) {
    return request(`/sys/v4/loyalty/online/redeem/preview`, {
      method: 'POST', body: JSON.stringify(payload)
    });
  },
  // Commit a redemption for a placed order. payload: { venueid, customerID, orderid, points?|reward_id?|surprise_id? }
  async loyaltyRedeemCommit(payload) {
    return request(`/sys/v4/loyalty/online/redeem/commit`, {
      method: 'POST', body: JSON.stringify(payload)
    });
  },
  // Award points for a placed order. payload: { venueid, customerID, orderid }
  async loyaltyEarn(payload) {
    return request(`/sys/v4/loyalty/online/earn`, {
      method: 'POST', body: JSON.stringify(payload)
    });
  },

  // Get promo codes and service fees
  async getPromoData(venueid) {
    return request(`/sys/v4/venue/getPromoData?venueid=${venueid}`);
  },

  // Validate promo code
  async validatePromo(venueid, promo_code, subtotal) {
    return request('/sys/v4/promo/validate', {
      method: 'POST',
      body: JSON.stringify({
        venueid,
        promo_code,
        subtotal
      })
    });
  },

  // Customer lookup by phone
  async lookupCustomerByPhone(phone, venueid) {
    return request('/sys/v4/customer/lookupByPhone', {
      method: 'POST',
      body: JSON.stringify({ phone, venueid })
    });
  },

  // Customer search (legacy - searches by multiple fields)
  async searchCustomer(venueid, search_value) {
    return request('/sys/v4/customer/searchByValue', {
      method: 'POST',
      body: JSON.stringify({ venueid, search_value })
    });
  },

  // Create or update customer
  async updateCustomer(customerData) {
    return request('/sys/v4/customer/updateRecord', {
      method: 'POST',
      body: JSON.stringify(customerData)
    });
  },

  // Get customer order history
  async getCustomerHistory(venueid, guestID) {
    return request(`/sys/v4/customer/getHistory?venueid=${venueid}&guestID=${guestID}`);
  },

  // Process credit card payment (fetches API key from venue settings)
  async processPayment(paymentData) {
    return request('/sys/v4/payments/processOnlinePayment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  },

  // Process gift card payment
  async processGiftCard(giftCardData) {
    return request('/sys/v4/payments/processGiftCardManually', {
      method: 'POST',
      body: JSON.stringify(giftCardData)
    });
  },

  // Get per-venue wallet config (which buttons to show + Google Pay tokenization)
  async getWalletConfig(venueid) {
    return request('/sys/v4/online/payments/wallet-config', {
      method: 'POST',
      body: JSON.stringify({ venueid })
    });
  },

  // Apple Pay merchant session validation.
  // Returns the RAW Apple merchant session object (NOT wrapped in {result:1}),
  // which is passed straight to session.completeMerchantValidation().
  async validateWalletSession({ venueid, validationURL, hostname }) {
    return request('/sys/v4/online/payments/wallet-session', {
      method: 'POST',
      body: JSON.stringify({ venueid, validationURL, hostname })
    });
  },

  // Process wallet payment (Apple Pay / Google Pay) — single-shot SecureAuth.
  // walletData: { venueid, wallet_type: 'APPLE_PAY'|'GOOGLE_PAY', paymentToken, amount }
  // Returns { result, transactionid, lastfour, authid, cardtype, message }.
  async processWalletPayment(walletData) {
    return request('/sys/v4/online/payments/wallet-sale', {
      method: 'POST',
      body: JSON.stringify(walletData)
    });
  },

  // Check gift card balance
  async checkGiftCardBalance(venueid, fullcard_number) {
    return request('/sys/v4/payments/checkGiftCardBalance', {
      method: 'POST',
      body: JSON.stringify({ venueid, fullcard_number })
    });
  },

  // Submit online order
  async submitOrder(orderData) {
    return request('/sys/v4/orders/submitOnlineOrder', {
      method: 'POST',
      body: JSON.stringify(orderData)
    });
  },

  // Legacy: Update/create online order
  async updateOrderOnline(orderData) {
    return request('/sys/v4/orders/updateOrderOnline', {
      method: 'POST',
      body: JSON.stringify(orderData)
    });
  },

  // Get order confirmation data
  async getOrderData(orderid, venueid) {
    return request(`/sys/v4/orders/getRemoteOrderData?id=${orderid}-${venueid}`);
  },

  // ============================================================================
  // SLUG & RESTAURANT GROUP ENDPOINTS
  // ============================================================================

  // Resolve a slug to either a venue or restaurant group
  // Returns: { type: 'venue'|'group', venueid?, group?, venues? }
  async resolveSlug(slug) {
    return request(`/sys/v4/online/resolve/${encodeURIComponent(slug)}`);
  },

  // Get restaurant group with all its venues
  // Can pass restaurant_group_id (number) or group slug (string)
  async getRestaurantGroup(groupIdOrSlug) {
    return request(`/sys/v4/online/group/${encodeURIComponent(groupIdOrSlug)}`);
  },

  // Get all venues in a restaurant group with distances from a location
  async getGroupVenuesWithDistance(groupId, latitude, longitude) {
    return request('/sys/v4/online/group/venues-by-distance', {
      method: 'POST',
      body: JSON.stringify({ 
        restaurant_group_id: groupId, 
        latitude, 
        longitude 
      })
    });
  },

  // Get venue by slug
  async getVenueBySlug(slug) {
    return request(`/sys/v4/online/venue/${encodeURIComponent(slug)}`);
  },

  // ============================================================================
  // 2FA AUTHENTICATION ENDPOINTS
  // ============================================================================

  // Request SMS verification code
  async requestVerificationCode(phone, venueid) {
    return request('/sys/v4/online/auth/request-code', {
      method: 'POST',
      body: JSON.stringify({ phone, venueid })
    });
  },

  // Verify SMS code and get JWT token + customer data
  async verifyCode(phone, code, venueid) {
    return request('/sys/v4/online/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify({ phone, code, venueid })
    });
  },

  // Resend verification code
  async resendCode(phone, venueid) {
    return request('/sys/v4/online/auth/resend-code', {
      method: 'POST',
      body: JSON.stringify({ phone, venueid })
    });
  },

  // Logout (clear local token - JWT is stateless)
  logout() {
    localStorage.removeItem('stove_online_token');
    return Promise.resolve({ result: 1 });
  },

  // Get current customer profile (requires auth)
  async getProfile() {
    const token = localStorage.getItem('stove_online_token');
    return request('/sys/v4/online/customer/profile', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  // Update customer profile (requires auth)
  async updateProfile(profileData) {
    const token = localStorage.getItem('stove_online_token');
    return request('/sys/v4/online/customer/profile', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    });
  },

  // Get customer loyalty info (requires auth)
  async getLoyalty(venueid) {
    const token = localStorage.getItem('stove_online_token');
    return request(`/sys/v4/online/customer/loyalty?venueid=${venueid}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  // Get saved addresses (requires auth)
  async getSavedAddresses(customerID) {
    return request(`/sys/v4/online/customer/addresses?customerID=${customerID}`);
  },

  // Get customer addresses by customerID
  async getCustomerAddresses(customerID) {
    return request(`/sys/v4/customer/getAddresses?customerID=${customerID}`);
  },

  // Save new address to customer profile
  async saveCustomerAddress(addressData) {
    return request('/sys/v4/customer/saveAddress', {
      method: 'POST',
      body: JSON.stringify(addressData)
    });
  },

  // Update existing address
  async updateCustomerAddress(addressData) {
    return request('/sys/v4/customer/updateAddress', {
      method: 'POST',
      body: JSON.stringify(addressData)
    });
  },

  // Delete customer address
  async deleteCustomerAddress(recid, customerID) {
    return request('/sys/v4/customer/deleteAddress', {
      method: 'POST',
      body: JSON.stringify({ recid, customerID })
    });
  },

  // Get saved payment methods (requires auth)
  async getSavedPaymentMethods() {
    const token = localStorage.getItem('stove_online_token');
    return request('/sys/v4/online/customer/payment-methods', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }
};

export default api;